import React, { useEffect, useRef } from 'react';
import { showSuccessAlert, showWarningAlert, showErrorAlert, showLoadingAlert, closeLoadingAlert } from '../utils/alertService';

const jobApplicationStyles = `
/* Job Application Page Styling */
.job-app-page-wrapper {
  background-color: #ffffff;
  min-height: 100vh;
  color: #0f172a;
}

/* Hero Section */
.job-app-hero-section {
  padding: 8rem 0 4.5rem;
  position: relative;
  text-align: center;
  background: radial-gradient(circle at 50% 0%, rgba(196, 10, 10, 0.18) 0%, rgba(0, 0, 0, 0) 70%), #000000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.job-app-hero-title {
  font-size: 3.4rem;
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 1.2rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-family: var(--font-main);
}

.job-app-hero-subtitle {
  font-size: 1.1rem;
  color: #94a3b8;
  max-width: 760px;
  margin: 0 auto 3.5rem;
  line-height: 1.76;
  font-family: var(--font-main);
}

/* 4 Highlights Cards - Glassmorphism */
.job-app-highlights-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.4rem;
  max-width: 1120px;
  margin: 0 auto;
}

.job-app-highlight-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 1.35rem 1.4rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 1px 0 rgba(255, 255, 255, 0.15);
  position: relative;
  overflow: hidden;
}

.job-app-highlight-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
}

.job-app-highlight-card:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(196, 10, 10, 0.08) 100%);
  border-color: rgba(196, 10, 10, 0.5);
  box-shadow: 0 16px 36px rgba(196, 10, 10, 0.22), inset 0 1px 2px 0 rgba(255, 255, 255, 0.25);
  transform: translateY(-5px);
}

.job-app-highlight-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(196, 10, 10, 0.25) 0%, rgba(196, 10, 10, 0.08) 100%);
  border: 1px solid rgba(196, 10, 10, 0.35);
  color: #ff3333;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.job-app-highlight-text h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.2rem 0;
  font-family: var(--font-main);
}

.job-app-highlight-text p {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0;
  font-family: var(--font-main);
}

/* --- Main Layout Section (Left: Hiring Process | Right: Form) --- */
.job-app-main-section {
  padding: 5rem 0 7rem;
  background-color: #f8fafc;
}

.job-app-layout-grid {
  display: grid;
  grid-template-columns: 440px 1fr;
  gap: 3.5rem;
  align-items: start;
}

/* Left Column: Hiring Process */
.job-app-left-col {
  position: sticky;
  top: 110px;
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
}

.job-app-process-badge {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #c40a0a;
  margin-bottom: 0.6rem;
  background: rgba(196, 10, 10, 0.08);
  padding: 0.3rem 0.9rem;
  border-radius: 50px;
  border: 1px solid rgba(196, 10, 10, 0.2);
  width: fit-content;
}

.job-app-process-title {
  font-size: 2.1rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 0.6rem;
  line-height: 1.25;
  font-family: var(--font-main);
}

.job-app-process-subtitle {
  font-size: 0.96rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.job-app-vertical-steps {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.job-app-step-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1.3rem 1.4rem;
  display: flex;
  gap: 1.1rem;
  align-items: flex-start;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
}

.job-app-step-item:hover {
  border-color: #c40a0a;
  transform: translateX(4px);
  box-shadow: 0 8px 24px rgba(196, 10, 10, 0.1);
}

.job-app-step-item-num {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #000000;
  color: #ffffff;
  font-weight: 800;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1.5px solid #c40a0a;
}

.job-app-step-item-content h4 {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.3rem 0;
  font-family: var(--font-main);
}

.job-app-step-item-content p {
  font-size: 0.86rem;
  color: #64748b;
  line-height: 1.55;
  margin: 0;
}

/* Left Column Help Card */
.job-app-help-card {
  background: #0b0f19;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 1.4rem 1.5rem;
  color: #ffffff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
}

.job-app-help-card h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.4rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.job-app-help-card p {
  font-size: 0.84rem;
  color: #94a3b8;
  line-height: 1.55;
  margin: 0 0 0.8rem 0;
}

.job-app-help-email {
  color: #c40a0a;
  font-weight: 600;
  text-decoration: underline;
  font-size: 0.88rem;
}

/* Right Column: Form Container */
.job-app-right-col {
  width: 100%;
}

.job-app-root {
  width: 100%;
  font-family: 'Poppins', sans-serif;
}

.job-app-root .contact100-form {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  overflow: hidden;
  padding: 40px 45px 45px 45px;
  position: relative;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.06);
}

/* Form Header */
.job-app-root .form-header {
  text-align: center;
  padding: 10px 0 25px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 35px;
}

.job-app-root .form-header img {
  margin-bottom: 15px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  max-width: 170px;
  height: auto;
}

.job-app-root .form-header h1 {
  font-size: 30px;
  color: #C60C0D;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  line-height: 1.25;
}

.job-app-root .form-header p {
  font-size: 14.5px;
  color: #555;
  margin-top: 10px;
  line-height: 1.6;
  max-width: 95%;
  margin-left: auto;
  margin-right: auto;
}

/* Input Field Wrapper */
.job-app-root .wrap-input100 {
  width: 100%;
  position: relative;
  margin-bottom: 30px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-sizing: border-box;
  background: #ffffff;
  transition: all 0.25s ease;
}

/* Input and Textarea Styling */
.job-app-root .input100 {
  font-family: 'Poppins', sans-serif;
  color: #333;
  line-height: 1.2;
  font-size: 14px;
  display: block;
  width: 100%;
  background: transparent;
  height: 56px;
  padding: 16px 20px 11px 22px;
  border: none;
  outline: none;
  border-radius: 12px;
  box-sizing: border-box;
}

.job-app-root textarea.input100 {
  min-height: 110px;
  padding-top: 25px;
  padding-bottom: 15px;
  resize: vertical;
}

.job-app-root .input100:focus {
  border: none;
  box-shadow: none;
}

.job-app-root .wrap-input100.input-focused {
  border-color: #C60C0D;
  box-shadow: 0 0 0 2px rgba(198, 12, 13, 0.15);
}

/* Floating Labels */
.job-app-root .label-input100 {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  line-height: 1.5;
  position: absolute;
  pointer-events: none;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  transition: top 0.25s ease, font-size 0.25s ease, color 0.25s ease, background-color 0.25s ease, left 0.25s ease;
  background-color: transparent;
  padding: 0 4px;
  z-index: 1;
}

.job-app-root .wrap-input100.has-val:not(.input-focused) .label-input100 {
  top: 0px;
  font-size: 11.5px;
  color: #666;
  background-color: #ffffff;
  padding: 0 6px;
  left: 15px;
  transform: translateY(-50%);
}

.job-app-root .wrap-input100.input-focused .label-input100 {
  top: 0px;
  font-size: 11.5px;
  color: #C60C0D;
  background-color: #ffffff;
  padding: 0 6px;
  left: 15px;
  transform: translateY(-50%);
}

/* Select Styling */
.job-app-root select.input100 {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  padding-right: 40px;
  padding-top: 20px;
  padding-bottom: 8px;
}

.job-app-root select {
  transform: translateY(-3px);
}

/* Custom Select Arrow */
.job-app-root .wrap-input100.validate-input-select::after {
  content: "";
  position: absolute;
  right: 22px;
  top: 50%;
  width: 9px;
  height: 9px;
  border-right: 2.2px solid #888;
  border-bottom: 2.2px solid #888;
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
  z-index: 9;
  transition: border-color 0.2s ease;
}

.job-app-root .wrap-input100.input-focused.validate-input-select::after {
  border-color: #C60C0D;
}

/* Validation Popups */
.job-app-root .alert-validate {
  position: relative;
}

.job-app-root .alert-validate::before {
  content: attr(data-validate);
  position: absolute;
  max-width: 70%;
  background-color: #C60C0D;
  color: #fff;
  border-radius: 6px;
  padding: 5px 12px;
  top: 50%;
  transform: translateY(-50%);
  right: calc(100% + 20px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  visibility: hidden;
  font-size: 13px;
  font-weight: 500;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.job-app-root .alert-validate::after {
  content: "!";
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: -30px;
  transform: translateY(-50%);
  color: #ffffff;
  background-color: #C60C0D;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 13px;
  line-height: 1;
  opacity: 0;
  visibility: hidden;
  z-index: 11;
  text-align: center;
}

.job-app-root .alert-validate.show-validation::before,
.job-app-root .alert-validate.show-validation::after {
  opacity: 1;
  visibility: visible;
}

.job-app-root .wrap-input100.show-validation,
.job-app-root .file-upload-wrapper.show-validation {
  border-color: #C60C0D !important;
  box-shadow: 0 0 0 2px rgba(198, 12, 13, 0.2) !important;
}

.job-app-root .wrap-input100.show-validation::after {
  content: attr(data-validate);
  display: block;
  position: absolute;
  bottom: -20px;
  left: 10px;
  color: #C60C0D;
  font-size: 11.5px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* Two-column inputs */
.job-app-root .rs1-wrap-input100,
.job-app-root .rs2-wrap-input100 {
  width: 100%;
}

@media (min-width: 768px) {
  .job-app-root .rs1-wrap-input100 {
    width: calc(50% - 15px);
    margin-right: 30px;
    float: left;
  }

  .job-app-root .rs2-wrap-input100 {
    width: calc(50% - 15px);
    float: left;
  }
}

.job-app-root .clearfix::after {
  content: "";
  display: table;
  clear: both;
}

/* Switch Button Field */
.job-app-root .wrap-switch-input {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin-bottom: 30px;
  box-sizing: border-box;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  user-select: none;
  cursor: pointer;
}

.job-app-root .wrap-switch-input:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.job-app-root .wrap-switch-input:focus-within {
  border-color: #c40a0a;
  box-shadow: 0 0 0 2px rgba(196, 10, 10, 0.15);
}

.job-app-root .wrap-switch-input.switch-active {
  border-color: #e2e8f0;
}

.job-app-root .switch-label {
  font-family: 'Poppins', sans-serif;
  font-size: 13.5px;
  color: #334155;
  font-weight: 500;
  margin: 0;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 8px;
}

.job-app-root .switch-control-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.job-app-root .switch-state-badge {
  font-family: 'Poppins', sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 10px;
  transition: all 0.25s ease;
  user-select: none;
}

.job-app-root .switch-state-badge.badge-yes {
  background-color: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.job-app-root .switch-state-badge.badge-no {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

/* Modern Toggle Switch */
.job-app-root .toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.job-app-root .toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.job-app-root .toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 34px;
}

.job-app-root .toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.job-app-root .toggle-switch input:checked + .toggle-slider {
  background: linear-gradient(135deg, #c40a0a 0%, #e11d48 100%);
  box-shadow: 0 2px 8px rgba(196, 10, 10, 0.3);
}

.job-app-root .toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 0 2px rgba(196, 10, 10, 0.2);
}

.job-app-root .toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

/* File Upload */
.job-app-root .file-upload-wrapper {
  border: 2px dashed #C60C0D;
  border-radius: 14px;
  padding: 35px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  margin-bottom: 30px;
  background-color: #fafbfc;
  box-shadow: inset 0 0 0 1px rgba(198, 12, 13, 0.08);
}

.job-app-root .file-upload-wrapper:hover,
.job-app-root .file-upload-wrapper.dragover {
  background-color: #fff5f5;
  border-color: #a30b0b;
}

.job-app-root .file-upload-wrapper input[type="file"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 5;
}

.job-app-root .file-upload-wrapper p {
  margin: 0;
  color: #555;
  font-size: 15px;
  font-weight: 500;
}

.job-app-root .file-upload-wrapper #file-name {
  font-weight: 600;
  color: #C60C0D;
  margin-top: 10px;
  display: block;
  word-break: break-all;
}

/* Footer Actions */
.job-app-root .form-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 25px;
  align-items: center;
  width: 100%;
}

@media (min-width: 600px) {
  .job-app-root .form-actions {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.job-app-root .contact100-form-btn {
  font-family: 'Poppins', sans-serif;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 280px;
  height: 50px;
  background-color: #C60C0D;
  border-radius: 12px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
}

.job-app-root .contact100-form-btn:hover {
  background-color: #a30b0b;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(198, 12, 13, 0.3);
}

.job-app-root .btn-back-careers {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #C60C0D;
  text-decoration: none;
  font-size: 14.5px;
  padding: 12px 24px;
  border-radius: 12px;
  border: 1.5px solid #C60C0D;
  transition: all 0.3s ease;
  font-weight: 600;
  width: auto;
  min-width: 200px;
  margin: 0 0 0 auto;
  box-sizing: border-box;
}

.job-app-root .btn-back-careers:hover {
  background-color: #C60C0D;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(198, 12, 13, 0.25);
}

@media (max-width: 1200px) {
  .job-app-layout-grid {
    grid-template-columns: 380px 1fr;
    gap: 2.2rem;
  }
}

@media (max-width: 1024px) {
  .job-app-highlights-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .job-app-layout-grid {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }

  .job-app-left-col {
    position: static;
  }
}

@media (max-width: 768px) {
  .job-app-hero-section {
    padding: 6rem 0 3.5rem;
  }

  .job-app-hero-title {
    font-size: 2.4rem;
  }

  .job-app-hero-subtitle {
    font-size: 0.98rem;
  }

  .job-app-highlights-grid {
    grid-template-columns: 1fr;
  }

  .job-app-root .contact100-form {
    padding: 30px 20px 35px 20px;
    border-radius: 16px;
  }

  .job-app-root .form-header h1 {
    font-size: 24px;
  }
}
`;

function JobApplicationPage() {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const form = root.querySelector('#form');
    if (!form) return;

    const inputs = form.querySelectorAll('.input100:not([type="file"])');
    const fileInput = form.querySelector('#file');
    const fileNameDisplay = form.querySelector('#file-name');
    const fileUploadZone = form.querySelector('#file-upload-zone');
    const uploadedFileNameHidden = form.querySelector('#uploaded-file-name');

    const urlParams = new URLSearchParams(window.location.search);
    const jt = urlParams.get('jt');
    const p = urlParams.get('p');
    const isAdParam = urlParams.get('isAd');
    const isAd = isAdParam !== null && ['true', '1', 'yes'].includes(isAdParam.trim().toLowerCase());
    const isAdEl = form.querySelector('#isAd');
    if (isAdEl) {
      isAdEl.value = isAd ? 'true' : 'false';
    }

    // --- Onsite Switch Toggle Handler ---
    const onsiteToggle = form.querySelector('#onSiteToggle');
    const onsiteHidden = form.querySelector('#onSite');
    const onsiteBadge = form.querySelector('#onsite-badge');
    const onsiteWrap = form.querySelector('#wrap-onsite');

    function updateOnsiteState(isChecked) {
      if (onsiteHidden) onsiteHidden.value = isChecked ? 'Yes' : 'No';
      if (onsiteBadge) {
        onsiteBadge.textContent = isChecked ? 'Yes' : 'No';
        if (isChecked) {
          onsiteBadge.classList.add('badge-yes');
          onsiteBadge.classList.remove('badge-no');
        } else {
          onsiteBadge.classList.add('badge-no');
          onsiteBadge.classList.remove('badge-yes');
        }
      }
      if (onsiteWrap) {
        if (isChecked) {
          onsiteWrap.classList.add('switch-active');
        } else {
          onsiteWrap.classList.remove('switch-active');
        }
      }
    }

    const onOnsiteToggleChange = (e) => {
      updateOnsiteState(e.target.checked);
    };

    const onOnsiteWrapClick = (e) => {
      if (onsiteToggle && e.target !== onsiteToggle && !e.target.closest('.toggle-switch')) {
        onsiteToggle.checked = !onsiteToggle.checked;
        updateOnsiteState(onsiteToggle.checked);
      }
    };

    if (onsiteToggle) {
      onsiteToggle.addEventListener('change', onOnsiteToggleChange);
    }
    if (onsiteWrap) {
      onsiteWrap.addEventListener('click', onOnsiteWrapClick);
    }

    function createSubmitHiddenInput(name, value) {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = name;
      hiddenInput.value = value;
      hiddenInput.dataset.generated = 'true';
      form.appendChild(hiddenInput);
    }

    function disablePrefilledField(element) {
      if (!element) return;
      const parent = element.closest('.wrap-input100');
      if (parent) {
        parent.classList.add('has-val');
      }
      createSubmitHiddenInput(element.name, element.value);
      element.setAttribute('disabled', 'disabled');
      element.style.cursor = 'not-allowed';
      element.style.backgroundColor = '#f8fafc';
      element.style.color = '#1e293b';
      element.style.fontWeight = '600';
    }

    if (jt) {
      const designation = form.querySelector('#designation');
      if (designation) {
        let titleVal = decodeURIComponent(jt);
        if (jt === 'interna') {
          titleVal = 'International Sales Executive';
          const select = form.querySelector('#platform');
          if (select) {
            select.value = 'WhatsApp';
            disablePrefilledField(select);
          }
          if (isAdEl) isAdEl.value = 'true';
        } else if (jt.includes('-') && !jt.includes(' ')) {
          titleVal = jt.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        }
        designation.value = titleVal;
        disablePrefilledField(designation);
      }
    }

    if (p) {
      const normalizedPt = p.toLowerCase().replace(/[^a-z0-9]/g, '');
      const select = form.querySelector('#platform');
      if (select) {
        const options = Array.from(select.options);
        for (let option of options) {
          const normalizedOption = option.value.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (normalizedOption === normalizedPt) {
            select.value = option.value;
            disablePrefilledField(select);
            break;
          }
        }
      }
    }

    // --- Floating Label & Focus Logic ---
    function checkValue(input) {
      const parent = input.closest('.wrap-input100');
      if (parent) {
        if (input.value.trim() !== '' || (input.tagName === 'SELECT' && input.value !== '')) {
          parent.classList.add('has-val');
        } else {
          parent.classList.remove('has-val');
        }
      }
    }

    inputs.forEach((input) => {
      const parent = input.closest('.wrap-input100');
      if (parent) {
        input.addEventListener('focus', () => {
          parent.classList.add('input-focused');
        });
        input.addEventListener('blur', function () {
          parent.classList.remove('input-focused');
          checkValue(this);
          validate(this);
        });
        if (input.tagName === 'SELECT') {
          input.addEventListener('change', function () {
            checkValue(this);
            validate(this);
          });
        }
        checkValue(input);
      }
    });

    // --- Form Validation Logic ---
    function validate(input) {
      const parent =
        input.closest('.validate-input') ||
        input.closest('.validate-phone') ||
        input.closest('.validate-input-select');
      if (!parent) return true;

      let isValid = true;
      const originalValidationMessage = parent.getAttribute('data-validate');

      if (input.hasAttribute('required')) {
        if (
          input.type === 'text' ||
          input.type === 'textarea' ||
          input.type === 'number' ||
          input.type === 'email' ||
          input.type === 'tel'
        ) {
          if (input.value.trim() === '') {
            isValid = false;
          }
        } else if (input.tagName === 'SELECT') {
          if (input.value === '' || input.value === null) {
            isValid = false;
          }
        } else if (input.type === 'file') {
          if (input.files.length === 0) {
            isValid = false;
          }
        }
      }

      if (isValid && input.type === 'email') {
        if (
          !input.value
            .trim()
            .match(
              /^([a-zA-Z0-9_\-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{1,5}|[0-9]{1,3})(\]?)$/
            )
        ) {
          isValid = false;
          parent.setAttribute('data-validate', 'Please enter a valid email address.');
        } else {
          parent.setAttribute('data-validate', originalValidationMessage);
        }
      }
      if (isValid && input.type === 'tel') {
        let cleaned = input.value.trim().replace(/[\s\-()]/g, '');
        if (cleaned.startsWith('+92')) cleaned = '0' + cleaned.slice(3);
        else if (cleaned.startsWith('92') && cleaned.length === 12) cleaned = '0' + cleaned.slice(2);
        input.value = cleaned;

        if (!cleaned.match(/^03\d{9}$/)) {
          isValid = false;
          parent.setAttribute('data-validate', 'Phone number must be in 03xxxxxxxx format.');
        } else {
          parent.setAttribute('data-validate', originalValidationMessage);
        }
      }

      if (!isValid) {
        parent.classList.add('show-validation');
        return false;
      } else {
        parent.classList.remove('show-validation');
        parent.setAttribute('data-validate', originalValidationMessage);
        return true;
      }
    }

    // --- File Upload (Drag & Drop) Logic ---
    function preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (fileUploadZone) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
        fileUploadZone.addEventListener(eventName, preventDefaults, false);
      });

      fileUploadZone.addEventListener('dragenter', () => fileUploadZone.classList.add('dragover'), false);
      fileUploadZone.addEventListener('dragover', () => fileUploadZone.classList.add('dragover'), false);
      fileUploadZone.addEventListener('dragleave', () => fileUploadZone.classList.remove('dragover'), false);
      fileUploadZone.addEventListener('drop', (e) => {
        fileUploadZone.classList.remove('dragover');
        const dt = e.dataTransfer;
        const files = dt.files;
        handleFiles(files);
      }, false);

      if (fileInput) {
        fileInput.addEventListener('change', (e) => {
          handleFiles(e.target.files);
        }, false);
      }

      function handleFiles(files) {
        const parent = fileUploadZone.closest('.validate-input');
        const originalValidationMessage = parent ? parent.getAttribute('data-validate') : '';

        if (files && files.length > 0) {
          const file = files[0];
          const ext = file.name.split('.').pop().toLowerCase();
          const validExts = ['pdf', 'doc', 'docx'];
          const validTypes = [
            'application/pdf',
            'application/x-pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          ];

          if (validTypes.includes(file.type) || validExts.includes(ext)) {
            if (fileNameDisplay) fileNameDisplay.textContent = file.name;
            if (uploadedFileNameHidden) uploadedFileNameHidden.value = file.name;

            // Attach dropped file directly to underlying file input
            if (fileInput) {
              try {
                const dataTransfer = new DataTransfer();
                for (let i = 0; i < files.length; i++) {
                  dataTransfer.items.add(files[i]);
                }
                fileInput.files = dataTransfer.files;
              } catch (dtErr) {
                fileInput.files = files;
              }
            }

            if (parent) {
              parent.classList.remove('show-validation');
              parent.setAttribute('data-validate', originalValidationMessage);
            }
          } else {
            if (fileNameDisplay) fileNameDisplay.textContent = 'Invalid file type. (PDF, DOC, DOCX only)';
            if (uploadedFileNameHidden) uploadedFileNameHidden.value = '';
            if (fileInput) fileInput.value = '';
            if (parent) {
              parent.classList.add('show-validation');
              parent.setAttribute('data-validate', 'Invalid file type. Please upload PDF, DOC, or DOCX.');
            }
          }
        } else {
          if (fileNameDisplay) fileNameDisplay.textContent = 'No file chosen';
          if (uploadedFileNameHidden) uploadedFileNameHidden.value = '';
          if (parent) {
            parent.classList.remove('show-validation');
            parent.setAttribute('data-validate', originalValidationMessage);
          }
        }
        if (fileInput) validate(fileInput);
      }
    }

    // --- Form Submission Handler ---
    let isSubmitting = false;

    const onSubmit = async function (e) {
      e.preventDefault();
      if (isSubmitting) return;

      let isFormValid = true;

      inputs.forEach((input) => {
        if (!validate(input)) {
          isFormValid = false;
        }
      });
      if (fileInput && !validate(fileInput)) {
        isFormValid = false;
      }

      if (isFormValid) {
        const formData = new FormData(form);

        if (!fileInput || fileInput.files.length === 0) {
          showWarningAlert('Resume Required', 'Please select or drag & drop your CV / Resume file before submitting.');
          return;
        }

        // Attach the real file and form metadata into formData
        formData.set('file', fileInput.files[0]);
        formData.set('form_type', 'Job Application');
        formData.set('file_name', fileInput.files[0].name);

        const submitBtn = form.querySelector('#submit-btn');
        const statusBtn = form.querySelector('#status');
        if (submitBtn) submitBtn.disabled = true;
        if (statusBtn) statusBtn.textContent = 'Uploading resume & sending...';

        showLoadingAlert('Submitting Application...', 'Please wait while we upload your CV and process your application.');
        isSubmitting = true;

        try {
          // Dedicated single submission to the canonical Job Applications backend endpoint.
          // This performs:
          // 1. Cloudinary upload for the CV
          // 2. Exactly ONE database insert into JobApplications (CRM)
          // 3. Exactly ONE row appended to Google Sheets
          // 4. Asynchronous HR notification email via SMTP
          const response = await fetch('https://api.expertdesignhub.com/noAuth/post-job-application', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error(`Server returned HTTP ${response.status}`);
          }

          closeLoadingAlert();
          showSuccessAlert(
            'Application Submitted!',
            'Thank you! Your details and resume have been successfully forwarded to our HR department.'
          );
          form.reset();
          if (onsiteToggle) {
            onsiteToggle.checked = true;
            updateOnsiteState(true);
          }
          if (fileNameDisplay) fileNameDisplay.textContent = 'No file chosen';
          form.querySelectorAll('.show-validation').forEach((el) => el.classList.remove('show-validation'));
        } catch (error) {
          console.error('Job application submission error:', error);
          closeLoadingAlert();
          showErrorAlert(
            'Submission Failed',
            'We encountered an issue submitting your application. Please check your connection or contact HR directly at info@expertscommunication.net.'
          );
        } finally {
          isSubmitting = false;
          if (statusBtn) statusBtn.textContent = 'Submit job application';
          if (submitBtn) submitBtn.disabled = false;
        }
      } else {
        showWarningAlert(
          'Incomplete Details',
          'Please fill in all required fields and correct the highlighted errors.'
        );
        const firstInvalid = form.querySelector('.show-validation input, .show-validation select, .show-validation textarea, .show-validation');
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (firstInvalid.focus) firstInvalid.focus();
        }
      }
    };

    form.addEventListener('submit', onSubmit);

    return () => {
      form.removeEventListener('submit', onSubmit);
      if (onsiteToggle) {
        onsiteToggle.removeEventListener('change', onOnsiteToggleChange);
      }
      if (onsiteWrap) {
        onsiteWrap.removeEventListener('click', onOnsiteWrapClick);
      }
    };
  }, []);

  return (
    <div className="job-app-page-wrapper" ref={containerRef}>
      <style>{jobApplicationStyles}</style>

      {/* Hero Section */}
      <section className="job-app-hero-section">
        <div className="container">
          <h1 className="job-app-hero-title">
            Join Our Dynamic <span style={{ color: '#c40a0a' }}>Team!</span>
          </h1>

          <p className="job-app-hero-subtitle">
            We're constantly looking for passionate individuals to grow with us. Tell us about yourself, your career aspirations, and let's build something great together.
          </p>

          {/* 4 Trust Cards */}
          <div className="job-app-highlights-grid">
            <div className="job-app-highlight-card">
              <div className="job-app-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="job-app-highlight-text">
                <h4>Inclusive Culture</h4>
                <p>Collaborative and dynamic growth</p>
              </div>
            </div>

            <div className="job-app-highlight-card">
              <div className="job-app-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="job-app-highlight-text">
                <h4>Career Acceleration</h4>
                <p>Mentorship and promotion tracks</p>
              </div>
            </div>

            <div className="job-app-highlight-card">
              <div className="job-app-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              </div>
              <div className="job-app-highlight-text">
                <h4>Modern Workplace</h4>
                <p>State-of-the-art office &amp; tech stack</p>
              </div>
            </div>

            <div className="job-app-highlight-card">
              <div className="job-app-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="job-app-highlight-text">
                <h4>Fast Response</h4>
                <p>Prompt candidate application review</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area: Left = Hiring Process | Right = Form */}
      <section className="job-app-main-section">
        <div className="container">
          <div className="job-app-layout-grid">
            
            {/* Left Side: Hiring Process & Info */}
            <aside className="job-app-left-col">
              <div>
                <span className="job-app-process-badge">Hiring Journey</span>
                <h2 className="job-app-process-title">How Our Hiring Process Works</h2>
                <p className="job-app-process-subtitle">
                  We ensure a smooth, transparent recruitment experience from your first application to your first day.
                </p>
              </div>

              {/* Vertical Process Steps */}
              <div className="job-app-vertical-steps">
                <div className="job-app-step-item">
                  <div className="job-app-step-item-num">01</div>
                  <div className="job-app-step-item-content">
                    <h4>Submit Application</h4>
                    <p>Fill out the application form on the right and attach your updated CV/Resume.</p>
                  </div>
                </div>

                <div className="job-app-step-item">
                  <div className="job-app-step-item-num">02</div>
                  <div className="job-app-step-item-content">
                    <h4>Profile Screening</h4>
                    <p>Our HR recruiters review your qualifications, technical skills, and experience level.</p>
                  </div>
                </div>

                <div className="job-app-step-item">
                  <div className="job-app-step-item-num">03</div>
                  <div className="job-app-step-item-content">
                    <h4>Interview &amp; Skill Check</h4>
                    <p>Structured discussion with team leads to evaluate mutual synergy and expertise.</p>
                  </div>
                </div>

                <div className="job-app-step-item">
                  <div className="job-app-step-item-num">04</div>
                  <div className="job-app-step-item-content">
                    <h4>Offer &amp; Onboarding</h4>
                    <p>Receive your formal offer and welcome kit to begin your journey with us.</p>
                  </div>
                </div>
              </div>

              {/* Assistance Box */}
              <div className="job-app-help-card">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Need Application Help?
                </h4>
                <p>
                  Have any queries regarding open positions or interview timelines? Reach our talent team:
                </p>
                <a href="mailto:info@expertscommunication.net" className="job-app-help-email" style={{ display: 'block', marginBottom: '8px' }}>
                  info@expertscommunication.net
                </a>
                <a href="tel:+923177770002" className="job-app-help-phone" style={{ color: '#ffffff', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.92rem' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  +92 (317) 777 0002
                </a>
              </div>
            </aside>

            {/* Right Side: Form (100% Original Elements Intact) */}
            <main className="job-app-right-col">
              <div className="job-app-root">
                <form
                  className="contact100-form validate-form"
                  name="google-sheet"
                  encType="multipart/form-data"
                  id="form"
                  autoComplete="on"
                  action="#"
                  method="POST"
                >
                  <input type="hidden" name="allowSubmit" id="allowSubmit" />
                  <input type="hidden" id="isAd" name="isAd" value="false" />

                  <div className="clearfix">
                    <div
                      className="wrap-input100 rs1-wrap-input100 validate-input"
                      data-validate="Please provide position applied for"
                    >
                      <label className="label-input100" htmlFor="designation">
                        Position Applied For
                      </label>
                      <input id="designation" className="input100" type="text" name="designation" maxLength="100" required />
                    </div>
                    <div
                      className="wrap-input100 rs2-wrap-input100 validate-input-select"
                      data-validate="How you reach us?"
                    >
                      <label className="label-input100" htmlFor="platform">
                        How you reach us?
                      </label>
                      <select id="platform" name="platform" className="input100" required defaultValue="">
                        <option value="" disabled></option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Indeed">Indeed</option>
                        <option value="WhatsApp">WhatsApp</option>
                        <option value="Referral">Referral</option>
                        <option value="Company Website">Company Website</option>
                        <option value="Email">Email</option>
                        <option value="Google Search">Google Search</option>
                        <option value="Job Fair">Job Fair</option>
                        <option value="Walk-in">Walk-in</option>
                        <option value="Recruiter Call">Recruiter Call</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="wrap-input100 validate-input" data-validate="Please enter your full name">
                    <label className="label-input100" htmlFor="name">
                      Full Name
                    </label>
                    <input id="name" className="input100" type="text" name="name" maxLength="50" required />
                  </div>

                  <div className="clearfix">
                    <div
                      className="wrap-input100 rs1-wrap-input100 validate-phone"
                      data-validate="Contact number must be in 03xxxxxxxx format."
                    >
                      <label className="label-input100" htmlFor="phone">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" className="input100" maxLength="11" required />
                    </div>
                    <div
                      className="wrap-input100 rs2-wrap-input100 validate-input"
                      data-validate="Please provide a valid email address"
                    >
                      <label className="label-input100" htmlFor="email">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" className="input100" required />
                    </div>
                  </div>

                  <div
                    className="wrap-input100 validate-input-select"
                    data-validate="Please select your experience level"
                  >
                    <label className="label-input100" htmlFor="experience">
                      Experience
                    </label>
                    <select id="experience" name="experience" className="input100" required defaultValue="">
                      <option value="" disabled></option>
                      <option value="0 - 6 month(s)">0 - 6 month(s)</option>
                      <option value="1 - 2 year(s)">1 - 2 year(s)</option>
                      <option value="More than 2 years">More than 2 years</option>
                    </select>
                  </div>

                  <div className="clearfix">
                    <div
                      className="wrap-input100 rs1-wrap-input100 validate-input"
                      data-validate="Please provide your current salary"
                    >
                      <label className="label-input100" htmlFor="currentSalary">
                        Current Salary (PKR)
                      </label>
                      <input type="number" id="currentSalary" name="currentSalary" className="input100" required />
                    </div>
                    <div
                      className="wrap-input100 rs2-wrap-input100 validate-input"
                      data-validate="Please provide your expected salary"
                    >
                      <label className="label-input100" htmlFor="expectedSalary">
                        Expected Salary (PKR)
                      </label>
                      <input type="number" id="expectedSalary" name="expectedSalary" className="input100" required />
                    </div>
                  </div>

                  <div className="clearfix">
                    <div
                      className="wrap-input100 rs1-wrap-input100 wrap-switch-input switch-active"
                      id="wrap-onsite"
                    >
                      <label className="switch-label" htmlFor="onSiteToggle">
                        Comfortable to work onsite
                      </label>
                      <div className="switch-control-group">
                        <span className="switch-state-badge badge-yes" id="onsite-badge">
                          Yes
                        </span>
                        <label className="toggle-switch" htmlFor="onSiteToggle">
                          <input
                            type="checkbox"
                            id="onSiteToggle"
                            name="onSiteToggle"
                            defaultChecked={true}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <input type="hidden" id="onSite" name="onSite" value="Yes" />
                    </div>

                    <div
                      className="wrap-input100 rs2-wrap-input100 validate-input-select"
                      data-validate="Please indicate your timing feasibility"
                    >
                      <label className="label-input100" htmlFor="timing">
                        Timing Feasibility
                      </label>
                      <select id="timing" name="timing" className="input100" required defaultValue="">
                        <option value="" disabled></option>
                        <option value="Yes">Feasible with timing</option>
                        <option value="No">Not feasible with timing</option>
                      </select>
                    </div>
                  </div>

                  <div
                    className="wrap-input100 validate-input"
                    data-validate="Please provide your current residential address"
                  >
                    <label className="label-input100" htmlFor="address">
                      Current Residential Address
                    </label>
                    <textarea id="address" className="input100" name="address" required></textarea>
                  </div>

                  <div
                    className="file-upload-wrapper validate-input"
                    id="file-upload-zone"
                    data-validate="Please attach your updated resume."
                  >
                    <input type="file" id="file" name="file" accept=".pdf,.doc,.docx" required />
                    <input type="hidden" id="uploaded-file-name" name="uploaded-file-name" />
                    <svg className="file-upload-icon" width="55" height="55" viewBox="0 0 24 24" fill="none" stroke="#C60C0D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 15px auto' }}>
                      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                      <path d="M12 12v9" />
                      <path d="m16 16-4-4-4 4" />
                    </svg>
                    <p>
                      Drag &amp; Drop your CV here or <span style={{ color: '#C60C0D', fontWeight: 700 }}>Click to Upload</span>
                    </p>
                    <p id="file-name">No file chosen</p>
                  </div>

                  <p style={{ fontSize: '13px', marginTop: '20px', textAlign: 'center', color: '#888' }}>
                    All fields are required. Incomplete applications might be rejected.
                  </p>

                  <div className="form-actions">
                    <button type="submit" className="contact100-form-btn" id="submit-btn">
                      <span id="status">Submit job application</span>
                    </button>
                    <a href="/careers/" className="btn-back-careers">
                      Back to Careers{' '}
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px' }}>
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  </div>
                </form>
              </div>
            </main>

          </div>
        </div>
      </section>
    </div>
  );
}

export default JobApplicationPage;
